'use client';

/* ──────────────────────────────────────────────
 *  BookingPaymentSection — inline Stripe payment
 *  for paid bookings. Mounted inside CheckoutStep
 *  once a pre-reservation exists.
 * ────────────────────────────────────────────── */

import { useCallback, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { ProPaymentStatus } from '@cfixe-monorepo/shared';
import { useBooking } from '../../context/BookingContext';
import { ProPaymentStripeForm } from '../../../pro-payment/components/ProPaymentStripeForm';
import { PaymentReassurance } from './PaymentReassurance';
import { REDIRECT_RETURN_PARAM, buildRedirectReturnUrl } from '../../../pro-payment/redirect-return';
import '../../../pro-payment/styles/pro-payment.scss';

interface BookingPaymentSectionProps {
  /**
   * Texte du bouton (spec Parcours § 7.6, `paymentButtonLabel`) : « Payer 36,00 € », « Payer l’acompte 10,80 € »,
   * une confirmation pour une empreinte ou une carte enregistrée. Absent : « Confirmer le rendez-vous ».
   */
  payLabel?: string;
  /** Disables the pay button until the user accepts the cancellation policy */
  disabled?: boolean;
  /** External loading state (e.g. the "pay on site" button is in progress) — shows the loading indicator and disables the pay button */
  busy?: boolean;
  /** Notifies the parent when the Stripe confirmation is processing */
  onProcessingChange?: (processing: boolean) => void;
  /** Rendered inside the Stripe form between the payment element and the pay button */
  extraContent?: ReactNode;
}

const POLL_INTERVAL_MS = 2_000;
const POLL_TIMEOUT_MS = 30_000;

export function BookingPaymentSection({
  payLabel,
  disabled,
  busy,
  onProcessingChange,
  extraContent,
}: BookingPaymentSectionProps = {}) {
  const { ctx, send, callbacks, labels, params, stripePublishableKey } = useBooking();
  const uuid = ctx.preReservationUuid;

  const [clientSecret, setClientSecret] = useState<string | undefined>(undefined);
  const [connectAccountId, setConnectAccountId] = useState<string | undefined>(undefined);
  /* Methods of the Stripe intent itself: a bank imprint / card save is card-only whatever the plan allows. */
  const [paymentMethodTypes, setPaymentMethodTypes] = useState<string[] | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  /* Tracks the in-flight status-poll timeout so it can be cancelled if the component unmounts. */
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  useEffect((): (() => void) => {
    mountedRef.current = true;
    return (): void => {
      mountedRef.current = false;
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, []);

  /* ── Load Stripe payment details (lazy intent creation backend-side) ── */
  useEffect((): (() => void) => {
    let cancelled = false;

    if (!uuid || !callbacks.onLoadPaymentDetails) {
      setLoading(false);
      setLoadError(true);
      return (): void => {
        cancelled = true;
      };
    }

    setLoading(true);
    setLoadError(false);

    callbacks
      .onLoadPaymentDetails(uuid)
      .then((details): void => {
        if (cancelled) return;
        setClientSecret(details.client_secret);
        setConnectAccountId(details.stripe_connect_account_id);
        setPaymentMethodTypes(details.payment_method_types);
      })
      .catch((): void => {
        if (cancelled) return;
        setLoadError(true);
      })
      .finally((): void => {
        if (cancelled) return;
        setLoading(false);
      });

    return (): void => {
      cancelled = true;
    };
  }, [uuid, callbacks]);

  /* ── After Stripe confirmation: poll until the payment is settled ── */
  const handleStripeSuccess = useCallback((): void => {
    if (!uuid) return;
    setFinalizing(true);

    const poll = callbacks.onPollPaymentStatus;
    if (!poll) {
      send({ type: 'PAYMENT_CONFIRMED', reservationUuid: uuid });
      return;
    }

    const start = Date.now();
    const tick = (): void => {
      poll(uuid)
        .then((res): void => {
          if (!mountedRef.current) return;
          const settled = res.status === ProPaymentStatus.PAYED || res.status === ProPaymentStatus.AUTHORIZED;
          /* Stripe already accepted the payment — proceed even if the webhook lags. */
          if (settled || Date.now() - start > POLL_TIMEOUT_MS) {
            send({ type: 'PAYMENT_CONFIRMED', reservationUuid: res.reservation_uuid ?? uuid });
            return;
          }
          pollTimeoutRef.current = setTimeout(tick, POLL_INTERVAL_MS);
        })
        .catch((): void => {
          if (!mountedRef.current) return;
          if (Date.now() - start > POLL_TIMEOUT_MS) {
            send({ type: 'PAYMENT_CONFIRMED', reservationUuid: uuid });
            return;
          }
          pollTimeoutRef.current = setTimeout(tick, POLL_INTERVAL_MS);
        });
    };
    pollTimeoutRef.current = setTimeout(tick, POLL_INTERVAL_MS);
  }, [uuid, callbacks, send]);

  /* ── Render ────────────────────────────────── */

  const returnUrl = useMemo(
    (): string | undefined => (uuid ? buildRedirectReturnUrl(REDIRECT_RETURN_PARAM.BOOKING, uuid) : undefined),
    [uuid],
  );

  if (finalizing) {
    return (
      <div className="bt-checkout__stripe-slot bt-payment-finalizing">
        <span className="bt-spinner bt-spinner--inline" />
        <span>{labels.payment_finalizing}</span>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bt-checkout__stripe-slot bt-payment-finalizing">
        <span className="bt-spinner bt-spinner--inline" />
      </div>
    );
  }

  if (loadError || !clientSecret || !connectAccountId || !stripePublishableKey) {
    return <p className="bt-error">{labels.error_generic}</p>;
  }

  /* Reassurance (secure badge + accepted-method icons) shown under the payment element, above the button.
   * Composed with the incoming extraContent (e.g. cancellation policy) since they share the same slot. */
  const paymentExtraContent = (
    <>
      {extraContent}
      <PaymentReassurance
        methods={paymentMethodTypes ?? params.allowed_payment_methods}
        secureLabel={labels.secure_payment}
        methodsLabel={labels.payment_methods_accepted}
      />
    </>
  );

  return (
    <div className="bt-checkout__stripe-slot">
      <ProPaymentStripeForm
        clientSecret={clientSecret}
        stripePublishableKey={stripePublishableKey}
        stripeConnectAccountId={connectAccountId}
        payLabel={payLabel ?? labels.confirm_appointment}
        errorLabel={labels.error_generic}
        disabled={disabled}
        busy={busy}
        extraContent={paymentExtraContent}
        returnUrl={returnUrl}
        onSuccess={handleStripeSuccess}
        onProcessingChange={onProcessingChange}
      />
    </div>
  );
}
